import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa um impairment (item da listagem paginada)
 * 
 * @typedef {Object} ImpairmentIndexResource
 * @property {number} id - Identificador único
 * @property {number} business_plan_id - ID do Business Plan
 * @property {string} nome_iniciativa - Nome da iniciativa
 * @property {string} versao_iniciativa - Versão da iniciativa
 * @property {string|null} dominio_iniciativa - Domínio da iniciativa
 * @property {string} descricao_iniciativa - Descrição da iniciativa
 * @property {string} data_inicio_iniciativa - Data de início da iniciativa
 * @property {string} data_fim_iniciativa - Data de fim da iniciativa
 * @property {string|null} conta_contabil_nome - Nome da conta contábil
 * @property {string|null} conta_contabil_codigo - Código da conta contábil
 * @property {string} empresa - Empresa
 * @property {string} encerramento_capitalizacao - Encerramento da capitalização
 * @property {string} data_avaliacao - Data da avaliação
 * @property {string} versao_avaliacao - Versão da avaliação
 * @property {Array} versoes_business_plan_avaliados - Versões do Business Plan avaliadas
 * @property {boolean} indicio_valor_recuperavel - Indício de valor recuperável
 * @property {boolean} indicio_mudanca_interna - Indício de mudança interna
 * @property {boolean} indicio_mudanca_externa - Indício de mudança externa
 * @property {boolean} indicador_perda_valor_ativo - Indicador de perda de valor do ativo
 * @property {boolean} indicador_mudanca_externa_adversa - Indicador de mudança externa adversa
 * @property {boolean} indicador_aumento_taxa_juros - Indicador de aumento da taxa de juros
 * @property {boolean} indicador_patrimonio_maior_que_valor_mercado - Indicador de patrimônio maior que valor de mercado
 * @property {string|null} descricao_indicio_fonte_externa - Descrição do indício de fonte externa
 * @property {boolean} indicador_obsolescencia_ativo - Indicador de obsolescência do ativo
 * @property {boolean} indicio_mudanca_utilizacao_ativo - Indício de mudança de utilização do ativo
 * @property {boolean} indicador_descontinuidade_ativo - Indicador de descontinuidade do ativo
 * @property {boolean} indicio_baixo_desempenho_ativo - Indício de baixo desempenho do ativo
 * @property {string|null} descricao_indicio_fonte_interna - Descrição do indício de fonte interna
 * @property {boolean} indicador_aumento_custos_operacionais - Indicador de aumento de custos operacionais
 * @property {boolean} indicador_fluxo_caixa_realizado_inferior - Indicador de fluxo de caixa realizado inferior
 * @property {boolean} indicador_queda_fluxo_caixa_orcado - Indicador de queda do fluxo de caixa orçado
 * @property {boolean} indicador_desvio_resultado_gerencial - Indicador de desvio de resultado gerencial
 * @property {string|null} valor_recuperavel_periodo - Período do valor recuperável (data)
 * @property {boolean} reducao_valor_recuperavel - Redução do valor recuperável
 * @property {string|null} reducao_valor_recuperavel_periodo - Período da redução do valor recuperável (data)
 * @property {string|null} fator_gerador_teste - Fator gerador do teste
 * @property {number} valor_contabil - Valor contábil
 * @property {number} saldo_capex - Saldo capex
 * @property {number} amortizacao_acumulada_anteriores - Amortização acumulada de anteriores
 * @property {number} impairment_acumulado_anteriores - Impairment acumulado de anteriores
 * @property {number} valor_recuperavel - Valor recuperável
 * @property {number} valor_liquido_despesas_venda - Valor líquido de despesas de venda
 * @property {number} valor_uso - Valor em uso
 * @property {number} diferenca_orcado_revisado - Diferença orçado revisado
 * @property {string} resultado_teste_impairment - Resultado do teste de impairment
 * @property {string|null} rxo - RXO
 * @property {string|null} descricao_resultado_final_avaliacao - Descrição do resultado final
 * @property {Array|null} memoria_calculo_e_evidencia - Memória de cálculo e evidências
 * @property {number} status - Status
 * @property {string} status_label - Label do status
 * @property {Array|null} calculos_vpl - Cálculos VPL (presente no show)
 */

/**
 * Representa um impairment detalhado (show)
 * 
 * @typedef {Object} ImpairmentShowResource
 * @property {number} id - Identificador único
 * @property {number} business_plan_id - ID do Business Plan
 * @property {string} nome_iniciativa - Nome da iniciativa
 * @property {string} versao_iniciativa - Versão da iniciativa
 * @property {string|null} dominio_iniciativa - Domínio da iniciativa
 * @property {string} descricao_iniciativa - Descrição da iniciativa
 * @property {string} data_inicio_iniciativa - Data de início da iniciativa
 * @property {string} data_fim_iniciativa - Data de fim da iniciativa
 * @property {string|null} conta_contabil_nome - Nome da conta contábil
 * @property {string|null} conta_contabil_codigo - Código da conta contábil
 * @property {string} empresa - Empresa
 * @property {string} encerramento_capitalizacao - Encerramento da capitalização
 * @property {string} data_avaliacao - Data da avaliação
 * @property {string} versao_avaliacao - Versão da avaliação
 * @property {Array} versoes_business_plan_avaliados - Versões do Business Plan avaliadas
 * @property {boolean} indicio_valor_recuperavel - Indício de valor recuperável
 * @property {boolean} indicio_mudanca_interna - Indício de mudança interna
 * @property {boolean} indicio_mudanca_externa - Indício de mudança externa
 * @property {boolean} indicador_perda_valor_ativo - Indicador de perda de valor do ativo
 * @property {boolean} indicador_mudanca_externa_adversa - Indicador de mudança externa adversa
 * @property {boolean} indicador_aumento_taxa_juros - Indicador de aumento da taxa de juros
 * @property {boolean} indicador_patrimonio_maior_que_valor_mercado - Indicador de patrimônio maior que valor de mercado
 * @property {string|null} descricao_indicio_fonte_externa - Descrição do indício de fonte externa
 * @property {boolean} indicador_obsolescencia_ativo - Indicador de obsolescência do ativo
 * @property {boolean} indicio_mudanca_utilizacao_ativo - Indício de mudança de utilização do ativo
 * @property {boolean} indicador_descontinuidade_ativo - Indicador de descontinuidade do ativo
 * @property {boolean} indicio_baixo_desempenho_ativo - Indício de baixo desempenho do ativo
 * @property {string|null} descricao_indicio_fonte_interna - Descrição do indício de fonte interna
 * @property {boolean} indicador_aumento_custos_operacionais - Indicador de aumento de custos operacionais
 * @property {boolean} indicador_fluxo_caixa_realizado_inferior - Indicador de fluxo de caixa realizado inferior
 * @property {boolean} indicador_queda_fluxo_caixa_orcado - Indicador de queda do fluxo de caixa orçado
 * @property {boolean} indicador_desvio_resultado_gerencial - Indicador de desvio de resultado gerencial
 * @property {string|null} valor_recuperavel_periodo - Período do valor recuperável (data)
 * @property {boolean} reducao_valor_recuperavel - Redução do valor recuperável
 * @property {string|null} reducao_valor_recuperavel_periodo - Período da redução do valor recuperável (data)
 * @property {string|null} fator_gerador_teste - Fator gerador do teste
 * @property {number} valor_contabil - Valor contábil
 * @property {number} saldo_capex - Saldo capex
 * @property {number} amortizacao_acumulada_anteriores - Amortização acumulada de anteriores
 * @property {number} impairment_acumulado_anteriores - Impairment acumulado de anteriores
 * @property {number} valor_recuperavel - Valor recuperável
 * @property {number} valor_liquido_despesas_venda - Valor líquido de despesas de venda
 * @property {number} valor_uso - Valor em uso
 * @property {number} diferenca_orcado_revisado - Diferença orçado revisado
 * @property {string} resultado_teste_impairment - Resultado do teste de impairment
 * @property {string|null} rxo - RXO
 * @property {string|null} descricao_resultado_final_avaliacao - Descrição do resultado final
 * @property {Array|null} memoria_calculo_e_evidencia - Memória de cálculo e evidências
 * @property {number} status - Status
 * @property {string} status_label - Label do status
 * @property {Array|null} calculos_vpl - Cálculos VPL (presente no show)
 */

/**
 * Representa o payload de criação de um impairment
 * 
 * @typedef {Object} ImpairmentCreatePayload
 * @property {number} business_plan_id - ID do Business Plan (obrigatório)
 */

const resource = {
  base: (empresaId) => `v1/ativacaoped/${empresaId}/impairments`,
  item: (empresaId, impairmentId) => `v1/ativacaoped/${empresaId}/impairments/${impairmentId}`
};

const LOADING_KEY = 'ativacaoped-impairments';

/**
 * Lista impairments com paginação.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object|string} [params] - Parâmetros de consulta (objeto) ou query string já montada
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<ImpairmentIndexResource>>} Lista paginada de impairments
 */
export const getImpairments = (empresaId, params = { page: 1, per_page: 10 }) => {
  // Aceita a query string crua montada pelo MasterDetail (page/per_page/sort/filters).
  const query = typeof params === "string" ? params : parseParams(params);
  let url = resource.base(empresaId);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Obtém um impairment específico pelo ID.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} impairmentId - ID do impairment
 * @returns {Promise<ImpairmentShowResource>} Dados do impairment
 */
export const getImpairment = (empresaId, impairmentId) => {
  const baseUrl = resource.item(empresaId, impairmentId);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data.data || data)
  );
};

/**
 * Lista Business Plans disponíveis para seleção de impairment.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<Array<{id: number, nome: string}>>} Lista de Business Plans disponíveis
 */
export const getSelecaoImpairments = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.base(empresaId).concat(`/selecao`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data.data || data)
  );
};

/**
 * Cria um novo impairment.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {ImpairmentCreatePayload} data - Dados do impairment (business_plan_id obrigatório)
 * @returns {Promise<ImpairmentIndexResource>} Impairment criado
 */
export const postImpairment = (empresaId, data) => {
  const baseUrl = resource.base(empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Atualiza um impairment existente (backend usa POST para update).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} impairmentId - ID do impairment a ser atualizado
 * @param {Object} data - Dados parciais do impairment para atualização
 * @returns {Promise<ImpairmentIndexResource>} Impairment atualizado
 */
export const putImpairment = (empresaId, impairmentId, data) => {
  const baseUrl = resource.item(empresaId, impairmentId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Exclui um impairment.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} impairmentId - ID do impairment a ser excluído
 * @returns {Promise<void>}
 */
export const deleteImpairment = (empresaId, impairmentId) => {
  const baseUrl = resource.item(empresaId, impairmentId);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl).then(({ data }) => data)
  );
};

/**
 * Cria uma nova versão do impairment.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} impairmentId - ID do impairment
 * @returns {Promise<Object>} Resultado da criação de nova versão
 */
export const postCriarNovaVersao = (empresaId, impairmentId) => {
  const baseUrl = resource.item(empresaId, impairmentId).concat(`/nova-versao`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl).then(({ data }) => data)
  );
};

/**
 * Calcula o impairment (nível de recuperabilidade).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} impairmentId - ID do impairment
 * @returns {Promise<Object>} Resultado do cálculo
 */
export const postCalcularImpairment = (empresaId, impairmentId) => {
  const baseUrl = resource.item(empresaId, impairmentId).concat(`/impairment`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl).then(({ data }) => data)
  );
};

/**
 * Faz upload de memória de cálculo e evidências (multipart: `arquivo`).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} impairmentId - ID do impairment
 * @param {FormData} formData - FormData com `arquivo` (file)
 * @returns {Promise<void>}
 */
export const postUpload = (empresaId, impairmentId, formData) => {
  const baseUrl = resource.item(empresaId, impairmentId).concat(`/upload`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  );
};

/**
 * Atualiza o nome de um arquivo de memória de cálculo.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} impairmentId - ID do impairment
 * @param {number|string} uploadId - ID do upload
 * @param {Object} data - Dados da atualização
 * @param {string} data.nome - Novo nome do arquivo
 * @returns {Promise<Object>} Resultado da atualização
 */
export const putUpload = (empresaId, impairmentId, uploadId, data) => {
  const baseUrl = resource.item(empresaId, impairmentId).concat(`/upload/${uploadId}`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Exclui um arquivo de memória de cálculo.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} impairmentId - ID do impairment
 * @param {number|string} uploadId - ID do upload a excluir
 * @returns {Promise<void>}
 */
export const deleteUpload = (empresaId, impairmentId, uploadId) => {
  const baseUrl = resource.item(empresaId, impairmentId).concat(`/upload/${uploadId}`);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl).then(({ data }) => data)
  );
};

export default {
  getImpairment,
  getImpairments,
  getSelecaoImpairments,
  postImpairment,
  putImpairment,
  deleteImpairment,
  postCriarNovaVersao,
  postCalcularImpairment,
  postUpload,
  putUpload,
  deleteUpload
};